/**
 * PATRON POINTS — choose a new PIN.
 *
 * Opened from the single-use link in the recovery email. The old PIN stops
 * working the moment the new one is saved; the points, the history and the
 * vouchers stay on the account.
 */
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, Check } from 'lucide-react';

const API = `${(typeof window !== 'undefined' ? window.location.origin : '')}/api/patron`;

export default function PatronPinReset() {
    const { t } = useTranslation();
    const [params] = useSearchParams();
    const token = params.get('token') || '';
    const cardId = params.get('card') || '';

    const [pin, setPin] = useState('');
    const [again, setAgain] = useState('');
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState('');

    const save = async () => {
        setBusy(true);
        setError('');
        try {
            const res = await fetch(`${API}/public/pin-reset/confirm`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, pin }),
            });
            if (res.ok) {
                setDone(true);
            } else {
                const body = await res.json().catch(() => ({}));
                setError(body.detail === 'pin_four_digits'
                    ? t('patronPin.errorFour', 'Your PIN must be 4 numbers.')
                    : t('patronPin.errorLink', 'This link has already been used or has run out. Ask for a new one.'));
            }
        } catch (_e) {
            setError(t('patronPin.errorLink', 'This link has already been used or has run out. Ask for a new one.'));
        } finally {
            setBusy(false);
        }
    };

    const ready = pin.length === 4 && pin === again;

    return (
        <div className="min-h-screen bg-slate-950 text-white flex items-start justify-center p-5">
            <div className="w-full max-w-md mt-10 space-y-5" data-testid="patron-pin-reset-page">
                <h1 className="text-2xl font-bold">{t('patronPin.title', 'Choose a new PIN')}</h1>

                {done ? (
                    <div className="rounded-2xl bg-emerald-500/10 border border-emerald-500/40 p-5 space-y-2"
                         data-testid="patron-pin-reset-done">
                        <p className="text-lg font-semibold flex items-center gap-2">
                            <Check className="w-5 h-5 text-emerald-400" />
                            {t('patronPin.doneTitle', 'Your new PIN is saved')}
                        </p>
                        <p className="text-sm text-white/80">
                            {t('patronPin.doneBody', 'Use your email and this new PIN next time you play. Your points are still there.')}
                        </p>
                        {cardId && (
                            <a href={`/goal/${cardId}`}
                               className="inline-flex min-h-[52px] items-center justify-center rounded-xl bg-emerald-500 px-5 font-semibold text-slate-950"
                               data-testid="patron-pin-reset-back">
                                {t('patronPin.backToGame', 'Back to the game')}
                            </a>
                        )}
                    </div>
                ) : (
                    <>
                        <p className="text-sm text-white/80">
                            {t('patronPin.help', 'Pick 4 numbers you will remember. Type them twice.')}
                        </p>
                        <input type="text" inputMode="numeric" maxLength={4} value={pin}
                               onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                               placeholder={t('patronPin.newPin', 'New PIN')}
                               className="w-full min-h-[56px] rounded-xl bg-white/10 px-4 text-xl tracking-[0.4em]"
                               data-testid="patron-pin-reset-new" />
                        <input type="text" inputMode="numeric" maxLength={4} value={again}
                               onChange={(e) => setAgain(e.target.value.replace(/\D/g, ''))}
                               placeholder={t('patronPin.againPin', 'Same PIN again')}
                               className="w-full min-h-[56px] rounded-xl bg-white/10 px-4 text-xl tracking-[0.4em]"
                               data-testid="patron-pin-reset-again" />
                        {pin && again && pin !== again && (
                            <p className="text-sm text-amber-400" data-testid="patron-pin-reset-mismatch">
                                {t('patronPin.mismatch', 'The two PINs are not the same.')}
                            </p>
                        )}
                        <button type="button" disabled={!ready || busy} onClick={save}
                                className="w-full min-h-[56px] rounded-xl bg-emerald-500 font-semibold text-slate-950 disabled:opacity-40"
                                data-testid="patron-pin-reset-save">
                            {busy ? <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                                : t('patronPin.save', 'Save my new PIN')}
                        </button>
                        {error && <p className="text-sm text-amber-400" data-testid="patron-pin-reset-error">{error}</p>}
                    </>
                )}
            </div>
        </div>
    );
}
